import type { LearningChangeType } from 'definitions';
import type { LibraryExperiment } from '@@/api/websites/get-website-experiments';
import type { ProgramOutcome } from '@@/api/program';
import { LEARNING_CHANGE_TYPES, LEARNING_CHANGE_TYPE_LABELS } from 'definitions';
import { matchesElementTheme } from '../learnings/library-element-theme';
import { CUSTOM_MECHANISM_LABEL, PREDEFINED_MECHANISMS, mechanismLabel, parseMechanisms } from './expected-mechanisms';

export { mechanismLabel };

/**
 * The library as a record of what has been learned, not only a list of what exists.
 *
 * Derived from fields experiments already carry (the hypothesis, the description, the
 * expected mechanisms, the concluded outcome) and, on a website with learnings on, from the
 * AI card (what each variant changed, its change types, the sentence on what the test
 * taught) and the team's debrief. Both feed the same three places: `searchText`, the facets
 * and the expanded row.
 *
 * A refused or failed card carries no sentence and no labels, so it adds nothing to a
 * search and matches no change type filter.
 */

export type LibraryRow = LibraryExperiment & {
  /** Everything a search should match, lowercased once per fetch rather than per keystroke. */
  searchText: string;
  /**
   * The Result column's sort key: the outcome first, then the lift. Sorting on the lift
   * alone put a deployment win (which carries no headline lift) below every loss.
   */
  resultSort: number | null;
};

/** Best first when sorted descending. No data sits below every reading that says something. */
const OUTCOME_RANK: Record<ProgramOutcome, number> = { won: 4, mixed: 3, inconclusive: 2, lost: 1, 'no-data': 0 };

export const OUTCOME_LABELS: Record<ProgramOutcome, string> = {
  won: 'Won',
  lost: 'Lost',
  mixed: 'Mixed',
  inconclusive: 'Inconclusive',
  'no-data': 'No data',
};

/**
 * What the card says about the test, for a search to match. Nothing for a card that could not be
 * written. The details are in (they say what the test did); the possible reason and the next test
 * are not, since they are suggestions, and a search for a word should find tests that did it.
 */
const insightSearchParts = ({ insight }: Pick<LibraryExperiment, 'insight'>): string[] => {
  if (!insight || insight.status !== 'generated') return [];
  return [
    insight.learning ?? '',
    ...insight.variants.flatMap((variant) => [
      variant.summary,
      variant.details ?? '',
      variant.elementName,
      ...variant.changeTypes.map((type) => LEARNING_CHANGE_TYPE_LABELS[type]),
    ]),
  ];
};

export function toLibraryRow(experiment: LibraryExperiment): LibraryRow {
  const mechanisms = parseMechanisms(experiment.expectedMechanisms);

  const parts = [
    experiment.name,
    experiment.hypothesis,
    experiment.description,
    ...mechanisms.selected.map(mechanismLabel),
    mechanisms.custom,
    ...(experiment.variants ?? []).map((variant) => variant.name),
    // Not "No data": searching "data layer" matched every test without a reading.
    experiment.reading && experiment.reading.outcome !== 'no-data' ? OUTCOME_LABELS[experiment.reading.outcome] : '',
    ...insightSearchParts(experiment),
    experiment.teamRead?.learning ?? '',
  ];
  const { reading } = experiment;

  return {
    ...experiment,
    searchText: parts.filter(Boolean).join(' \n ').toLowerCase(),
    // Rank in the thousands, lift (a percentage, clamped) within it.
    resultSort: reading
      ? OUTCOME_RANK[reading.outcome] * 1000 + Math.max(-999, Math.min(999, reading.lift ?? 0))
      : null,
  };
}

export type LibraryFilters = {
  outcome: ProgramOutcome | '';
  mechanism: string;
  changeType: LearningChangeType | '';
  /** An element name the model wrote, accepted only when it is one the loaded rows carry. */
  element: string;
  /** A live theme's id, as it travels in the URL, accepted on the same terms. */
  theme: string;
};

/**
 * The mechanism filter value for "anything written in". One bucket rather than one
 * option per text: the free-text mechanism is typed per test, so listing each would
 * fill the filter with near-duplicates. The text itself is searched instead.
 *
 * Underscore-prefixed so it cannot collide with a predefined id, the same concern that
 * moved the stored value off the old `custom:` prefix (see expected-mechanisms.ts).
 */
export const WRITTEN_IN_MECHANISM = '_written-in';

const hasMechanism = (row: LibraryRow, mechanism: string) => {
  const mechanisms = parseMechanisms(row.expectedMechanisms);
  return mechanism === WRITTEN_IN_MECHANISM ? mechanisms.custom.trim() !== '' : mechanisms.selected.includes(mechanism);
};

/** Whether any variant of the test was labelled with this change type. Only a written card has labels. */
const hasChangeType = (row: LibraryRow, changeType: LearningChangeType) =>
  row.insight?.status === 'generated' &&
  row.insight.variants.some((variant) => variant.changeTypes.includes(changeType));

export function matchesFilters(
  row: LibraryRow,
  { outcome, mechanism, changeType, element, theme }: LibraryFilters,
): boolean {
  if (outcome && row.reading?.outcome !== outcome) return false;
  if (mechanism && !hasMechanism(row, mechanism)) return false;
  if (changeType && !hasChangeType(row, changeType)) return false;
  return matchesElementTheme(row, { element, theme });
}

export type Facet<K extends string> = { value: K; label: string; count: number };

/**
 * Only values that occur, in a fixed order, so the filter never offers an option that
 * empties the table and the list does not reshuffle as counts change.
 */
export function outcomeFacets(rows: readonly LibraryRow[]): Facet<ProgramOutcome>[] {
  return (Object.keys(OUTCOME_LABELS) as ProgramOutcome[])
    .map((value) => ({
      value,
      label: OUTCOME_LABELS[value],
      count: rows.filter((row) => row.reading?.outcome === value).length,
    }))
    .filter((facet) => facet.count > 0);
}

/** The predefined mechanisms, then one "Other" bucket for everything written in. */
export function mechanismFacets(rows: readonly LibraryRow[]): Facet<string>[] {
  const options = [
    ...PREDEFINED_MECHANISMS.map(({ id, label }) => ({ value: id, label })),
    { value: WRITTEN_IN_MECHANISM, label: CUSTOM_MECHANISM_LABEL },
  ];
  return options
    .map((option) => ({ ...option, count: rows.filter((row) => hasMechanism(row, option.value)).length }))
    .filter((facet) => facet.count > 0);
}

/** The change types that occur, in definition order, labelled; zero counts dropped like the other facets. */
export function changeTypeFacets(rows: readonly LibraryRow[]): Facet<LearningChangeType>[] {
  return LEARNING_CHANGE_TYPES.map((value) => ({
    value,
    label: LEARNING_CHANGE_TYPE_LABELS[value],
    count: rows.filter((row) => hasChangeType(row, value)).length,
  })).filter((facet) => facet.count > 0);
}
